/**
 * gEdit 组件库演示用本地资源路径
 * 根据 config.geditLocalDemoAssets 决定：
 *   true  = 使用本地相对路径（public 目录）
 *   false = 使用七牛云完整 URL（自动签名）
 */
import config from '../../config/config.js';
import baseFunc from '../../config/base_func.js';
import { getQiniuSignedUrl } from './qiniuMedia.js';

const LOCAL_MODE = config.geditLocalDemoAssets !== false;

const QINIU_HOST = (config.qiniuMediaDomain || '').replace(/^\/\//, '');
const QINIU_IMAGES = `https://${QINIU_HOST}/gedit/images/`;
const QINIU_AUDIO = `https://${QINIU_HOST}/gedit/audio/`;
const QINIU_VIDEO = `https://${QINIU_HOST}/gedit/video/`;
const QINIU_FILES = `https://${QINIU_HOST}/gedit/files/`;
const LOCAL_IMAGES = '/gedit/images/';
const LOCAL_AUDIO = '/gedit/audio/';
const LOCAL_VIDEO = '/gedit/video/';
const LOCAL_FILES = '/gedit/files/';

export function img(path) {
	if (LOCAL_MODE) {
		return `${LOCAL_IMAGES}${path}`;
	}
	return baseFunc.getQiniusign(`${QINIU_IMAGES}${path}`);
}
export function audio(path) {
	if (LOCAL_MODE) {
		return `${LOCAL_AUDIO}${path}`;
	}
	return baseFunc.getQiniusign(`${QINIU_AUDIO}${path}`);
}
export function video(path) {
	if (LOCAL_MODE) {
		return `${LOCAL_VIDEO}${path}`;
	}
	return baseFunc.getQiniusign(`${QINIU_VIDEO}${path}`);
}
export function file(path) {
	const encoded = encodeURIComponent(path);
	if (LOCAL_MODE) {
		return `${LOCAL_FILES}${encoded}`;
	}
	return baseFunc.getQiniusign(`${QINIU_FILES}${encoded}`);
}

/**
 * 按当前 geditLocalDemoAssets 解析演示图地址。
 * 区块数据里可能残留本地 /gedit/images/ 路径，切换七牛后展示时需重新解析。
 */
export function resolveGeditDemoImageUrl(url) {
	if (!url) return '';
	const raw = typeof url === 'string' ? url : (url.previewUrl || url.url || '');
	if (!raw) return '';
	if (raw.startsWith('blob:') || raw.startsWith('data:')) return raw;
	const match = String(raw).match(/\/gedit\/images\/([^/?#]+)/i);
	if (match) {
		return img(match[1]);
	}
	return getQiniuSignedUrl(raw);
}

export const LOCAL_ASSET = {
	// 头部组件
	headerHero: img('header-hero.jpg'),
	headerInfoCard: img('header-info-card.png'),
	headerInfoCardV2: img('header-info-card-v2.png'),
	headerIpBrand: img('header-ip-brand.png'),
	carousel: [
		img('carousel-1.jpg'),
		img('carousel-2.jpg'),
	],
	carouselImage: [
		img('carousel-image-1.png'),
		img('carousel-image-2.png'),
	],

	// 跳转链接
	qrcodeNavDefault: img('qrcode-nav-logo.png'),
	qrcodeNavDefaultPlus: img('qrcode-nav-logo-plus.png'),
	qrcodeNavCard: img('qrcode-nav-default.png'),
	qrcodeNavCell: img('qrcode-nav-item-4.png'),
	qrcodeNavItems: [
		img('qrcode-nav-item-1.png'),
		img('qrcode-nav-item-2.png'),
		img('qrcode-nav-item-3.png'),
	],
	qrcodeNavLink: [
		img('qrcode-nav-link-1.jpg'),
		img('qrcode-nav-link-2.jpg'),
		img('qrcode-nav-link-3.jpg'),
	],

	// 跳转快捷网格 8 张默认图标
	navGridItems: [
		img('nav-gird-1.jpg'),
		img('nav-gird-2.jpg'),
		img('nav-gird-3.jpg'),
		img('nav-gird-4.jpg'),
		img('nav-gird-5.jpg'),
		img('nav-gird-6.jpg'),
		img('nav-gird-7.jpg'),
		img('nav-gird-8.jpg'),
	],

	// 3列跳转默认图标
	quickNavGridItems: [
		img('quick-nav-grid-1.jpg'),
		img('quick-nav-grid-2.jpg'),
		img('quick-nav-grid-3.jpg'),
		img('quick-nav-grid-4.jpg'),
		img('quick-nav-grid-5.jpg'),
		img('quick-nav-grid-6.jpg'),
		img('quick-nav-grid-7.jpg'),
		img('quick-nav-grid-8.jpg'),
		img('quick-nav-grid-9.jpg'),
	],

	// 付费跳转（实战课程版）
	payJumpLinkBg: img('payjumplink1.png'),
	payJumpLinkUpdate: img('payjumplink2.png'),
	payJumpLinkCase: img('payjumplink4.png'),
	payJumpLinkReplay: img('payjumplink3.png'),
	// 付费跳转（变现进阶版）
	payJumpLinkAdvanceBg: img('payjumplink5.png'),

	// 图片网格
	imgGrid: [
		img('img-grid-1.png'),
		img('img-grid-2.png'),
		img('img-grid-3.png'),
		img('img-grid-4.png'),
	],

	// 图片卡片
	imgCard: [
		img('img-card-1.jpg'),
		img('img-card-2.jpg'),
		img('img-card-3.jpg'),
	],
	imgCardGallery: [
		img('img-card-1.png'),
		img('img-card-2.png'),
		img('img-card-3.png'),
		img('img-card-4.png'),
	],

	// 图文卡片
	imgFeatureCard: img('img-feature-card.png'),

	// 图片拼贴（左大图 + 右上 + 右下两小图）
	imgCollage: [
		img('img-collage-1.png'),
		img('img-collage-2.png'),
		img('img-collage-3.png'),
		img('img-collage-4.png'),
	],

	// 三图拼贴（左大图 + 右上下两图）
	imgTrio: [
		img('img-trio-1.png'),
		img('img-trio-2.png'),
		img('img-trio-3.png'),
	],

	// 图片四宫格
	imgQuad: [
		img('img-quad-1.png'),
		img('img-quad-2.png'),
		img('img-quad-3.png'),
		img('img-quad-4.png'),
		img('img-quad-5.png'),
		img('img-quad-6.png'),
		img('img-quad-7.png'),
		img('img-quad-8.png'),
	],

	// 图片马赛克
	imgMosaic: [
		img('img-mosaic-1.png'),
		img('img-mosaic-2.png'),
		img('img-mosaic-3.png'),
		img('img-mosaic-4.png'),
		img('img-mosaic-5.png'),
		img('img-mosaic-6.png'),
	],

	// 视频列表
	videoList: [
		img('video-list-1.jpg'),
		img('video-list-2.jpg'),
		img('video-list-3.jpg'),
		img('video-list-4.jpg'),
		img('video-list-5.jpg'),
		img('video-list-6.jpg'),
	],

	// 视频横排
	videoRow: [
		img('video-row-1.png'),
		img('video-row-2.png'),
		img('video-row-3.png'),
	],

	// 音频库 / 付费音频库默认音频（助眠主题）
	audioLibrary: [
		img('audio-lib-1.jpg'),
		img('audio-lib-2.jpg'),
		img('audio-lib-3.jpg'),
	],
	audioLibraryPresets: [
		{ filename: 'audio-lib-1.mp3', cover: img('audio-lib-1.jpg'), audioUrl: audio('audio-lib-1.mp3') },
		{ filename: 'audio-lib-2.mp3', cover: img('audio-lib-2.jpg'), audioUrl: audio('audio-lib-2.mp3') },
		{ filename: 'audio-lib-3.mp3', cover: img('audio-lib-3.jpg'), audioUrl: audio('audio-lib-3.mp3') },
	],

	// 音频播放器 / 付费音频播放器封面（星空海景）
	audioPlayerCover: img('pay-audio-player-cover.jpg'),
	audioPlayerPresets: {
		id: 'da-42001',
		filename: 'sleep1.mp3',
		coverFilename: 'pay-audio-player-cover.jpg',
		coverUrl: img('pay-audio-player-cover.jpg'),
		audioUrl: audio('sleep1.mp3'),
		duration: 102,
	},

	payAudioPlayer: audio('sleep1.mp3'),

	// 付费音频歌单（默认 4 首）
	payAudioPlaylist: [
		{
			id: 'da-22001',
			filename: 'Ariana Grande - hate that i made you love me',
			audio: audio('pay-audio-playlist-1.mp3'),
			cover: img('pay-audio-playlist-1.jpg'),
			coverFilename: 'pay-audio-playlist-1.jpg',
			duration: 198,
			filesize: '7.70 MB',
			filesize_byte: Math.round(7.7 * 1024 * 1024),
		},
		{
			id: 'da-22002',
			filename: 'Papa Tin - Una Mattina',
			audio: audio('pay-audio-playlist-2.mp3'),
			cover: img('pay-audio-playlist-2.jpg'),
			coverFilename: 'pay-audio-playlist-2.jpg',
			duration: 177,
			filesize: '6.9 MB',
			filesize_byte: Math.round(6.9 * 1024 * 1024),
		},
		{
			id: 'da-22003',
			filename: "DarrenHayes - Creepin up on You",
			audio: audio("pay-audio-playlist-3.mp3"),
			cover: img('pay-audio-playlist-3.jpg'),
			coverFilename: 'pay-audio-playlist-3.jpg',
			duration: 293,
			filesize: '11.4 MB',
			filesize_byte: Math.round(11.4 * 1024 * 1024),
		},
		{
			id: 'da-22004',
			filename: 'Ariana Grande - petal',
			audio: audio('pay-audio-playlist-4.mp3'),
			cover: img('pay-audio-playlist-4.jpg'),
			coverFilename: 'pay-audio-playlist-4.jpg',
			duration: 184,
			filesize: '7.18 MB',
			filesize_byte: Math.round(7.18 * 1024 * 1024),
		},
	],

	// 付费视频播放器
	payVideoPlayerCover: img('pay-video-player-1.png'),
	payVideoPlayerPresets: {
		filename: 'pay-video-player-1.mp4',
		coverFilename: 'pay-video-player-1.png',
		coverUrl: img('pay-video-player-1.png'),
		videoUrl: video('pay-video-player-1.mp4'),
	},

	// 付费视频列表 / 付费视频系列（各自独立数据源）
	payVideoListPresets: [
		{ id: 'dv-12001',filesize: 3806330, filesize_byte: Math.round(3.63 * 1024 * 1024), title: '阿勒泰的极光真的好美', description: '自然风光', duration: 9, videoUrl: 'pay-video-list-1.mp4', coverUrl: 'pay-video-list-1.png' },
		{ id: 'dv-12002',filesize: 1887436, filesize_byte: Math.round(1.80 * 1024 * 1024), title: '雪夜归途', description: '自然风光', duration: 12, videoUrl: 'pay-video-list-2.mp4', coverUrl: 'pay-video-list-2.png' },
		{ id: 'dv-12003',filesize: 7392460, filesize_byte: Math.round(7.05 * 1024 * 1024), title: '日落和海', description: '自然风光', duration: 9, videoUrl: 'pay-video-list-3.mp4', coverUrl: 'pay-video-list-3.png' },
		{ id: 'dv-12004',filesize: 6595543, filesize_byte: Math.round(6.29 * 1024 * 1024), title: '追星人的终极浪漫', description: '自然风光', duration: 19, videoUrl: 'pay-video-list-4.mp4', coverUrl: 'pay-video-list-4.png' },
	],


	// 付费图片墙
	payImageWall: [
		img('pay-image-wall-1.png'),
		img('pay-image-wall-2.png'),
		img('pay-image-wall-3.jpg'),
	],

	// 付费图片集（9张）
	payImageGallery: [
		img('pay-image-gallery-1.jpg'),
		img('pay-image-gallery-2.jpg'),
		img('pay-image-gallery-3.jpg'),
		img('pay-image-gallery-4.jpg'),
		img('pay-image-gallery-5.jpg'),
		img('pay-image-gallery-6.jpg'),
		img('pay-image-gallery-7.jpg'),
		img('pay-image-gallery-8.jpg'),
		img('pay-image-gallery-9.jpg'),
	],

	// 付费图片集（九宫格，9张）
	payImageGallery: [
		img('pay-image-gallery-1.jpg'),
		img('pay-image-gallery-2.jpg'),
		img('pay-image-gallery-3.jpg'),
		img('pay-image-gallery-4.jpg'),
		img('pay-image-gallery-5.jpg'),
		img('pay-image-gallery-6.jpg'),
		img('pay-image-gallery-7.jpg'),
		img('pay-image-gallery-8.jpg'),
		img('pay-image-gallery-9.jpg'),
	],

	// 付费文件解锁
	payFileUnlock: [
		img('pay-file-unlock-1.png'),
		img('pay-file-unlock-2.png'),
		img('pay-file-unlock-3.png'),
		img('pay-file-unlock-4.png'),
		img('pay-file-unlock-5.png'),
		img('pay-file-unlock-6.png'),
	],

	// 付费文件列表
	payFileList: [
		img('pay-file-list-1.png'),
		img('pay-file-list-2.png'),
		img('pay-file-list-3.png'),
		img('pay-file-list-4.png'),
		img('pay-file-list-5.png'),
		img('pay-file-list-6.png'),
	],

	// 付费文件合集
	payFileCollection: [
		{
			filename: 'AI客服智能体搭建手册.docx',
			filesize: '4.05 MB',
			filesize_byte: 4249578,
			url: file('AI客服智能体搭建手册.docx'),
		},
		{
			filename: '本地商家短视频脚本库.pptx',
			filesize: '2.76 MB',
			filesize_byte: 2893735,
			url: file('本地商家短视频脚本库.pptx'),
		},
		{
			filename: '矩阵账号运营计划表.xlsx',
			filesize: '13.24 KB',
			filesize_byte: 13556,
			url: file('矩阵账号运营计划表.xlsx'),
		},
		{
			filename: '虚拟资源项目执行方案.docx',
			filesize: '2.48 MB',
			filesize_byte: 2601152,
			url: file('虚拟资源项目执行方案.docx'),
		},
	],

	// 付费文件解析（mock）
	payFileParse: [
		img('pay-file-parse-0.jpg'),
		img('pay-file-parse-1.jpg'),
		img('pay-file-parse-2.jpg'),
		img('pay-file-parse-3.jpg'),
		img('pay-file-parse-4.jpg'),
		img('pay-file-parse-5.jpg'),
		img('pay-file-parse-6.jpg'),
		img('pay-file-parse-7.jpg'),
	],

	// 二维码占位
	qrcodeNone: img('qrcode-none.png'),

	// 模板列表手机外框
	iphoneFrame: img('iphone.png'),

	// 文件类型图标
	payFileIcons: {
		pdf: img('pdf.png'),
		word: img('word.png'),
		excel: img('excel.png'),
		powerpoint: img('powerpoint.png'),
		txt: img('txt.png'),
		zip: img('zip.png'),
		file: img('file.png'),
	},
};
